import { ConversationStatus } from '@provision/conversation-types';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { DynamicIcon, Tooltip } from 'src/components';
import {
  ConversationType,
  conversationTypeOptions,
  getStatusColor,
  getStatusDisplay,
  getUrgencyColor,
  getUrgencyDisplay,
} from 'src/graphql';
import { TextCell } from 'src/pages/CustomViews/custom-views-entities-table';
import { useDisplayTime } from 'src/utils/hooks';

export const TypeField = ({ type }: { type: string }) => {
  const text = useMemo(
    () =>
      conversationTypeOptions.find((i) => i.id === (type as ConversationType))?.label ?? '',
    [type],
  );

  return <TextCell text={text} />;
};

export const ConversationStatusField = ({
  statusId,
  snoozeUntil,
}: {
  statusId: string;
  snoozeUntil?: string | null;
}) => {
  const { displayTime } = useDisplayTime();
  const data = getStatusDisplay(statusId);

  const tooltipText = useMemo(() => {
    if ((statusId as ConversationStatus) === ConversationStatus.Snoozed && snoozeUntil) {
      const snoozeAt = dayjs(snoozeUntil);
      const datePart = snoozeAt.format('MMM D');
      return `Snoozed until ${datePart}, ${displayTime(snoozeAt)}`;
    }

    return statusId;
  }, [displayTime, snoozeUntil, statusId]);

  return (
    <Tooltip title={tooltipText}>
      <DynamicIcon
        name={data.icon}
        library={data.library}
        sx={{
          color: (theme) => getStatusColor(statusId, theme),
        }}
      />
    </Tooltip>
  );
};

export const ConversationUrgencyField = ({ urgencyId }: { urgencyId: string }) => {
  if (!urgencyId) {
    return '-';
  }

  const display = getUrgencyDisplay(urgencyId);

  return (
    <Tooltip title={urgencyId}>
      <DynamicIcon
        name={display.icon}
        library={display.library}
        sx={{
          color: getUrgencyColor(urgencyId),
        }}
      />
    </Tooltip>
  );
};
